import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { Portal } from './Portal';

export interface SelectOption<T = string> {
  label: string;
  value: T;
  disabled?: boolean;
}

export interface SelectProps<T = string> {
  options: SelectOption<T>[];
  value: T;
  onChange: (value: T) => void;
  placeholder?: string;
  searchable?: boolean;
  disabled?: boolean;
  className?: string;
}

/**
 * Custom select component with optional search functionality and Portal-based dropdown
 * Replaces native <select> with better UX, styling, and proper z-index layering
 */
export function Select<T = string>({
  options = [],
  value,
  onChange,
  placeholder = 'Select an option',
  searchable = false,
  disabled = false,
  className = '',
}: SelectProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const selectRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Get selected option label (defensive check for undefined options)
  const selectedOption = options?.find((opt) => opt.value === value);
  const selectedLabel = selectedOption?.label || placeholder;

  // Filter options based on search (defensive check for undefined options)
  const filteredOptions = searchable
    ? (options || []).filter((opt) =>
        opt.label.toLowerCase().includes(search.toLowerCase())
      )
    : (options || []);

  // Calculate dropdown position when opened
  useEffect(() => {
    if (isOpen && selectRef.current) {
      const rect = selectRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, [isOpen]);

  // Close dropdown when clicking outside (check both trigger and portal dropdown)
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      const clickedInsideTrigger = selectRef.current?.contains(target);
      const clickedInsideDropdown = dropdownRef.current?.contains(target);

      if (!clickedInsideTrigger && !clickedInsideDropdown) {
        setIsOpen(false);
        setSearch('');
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, searchable]);

  const handleSelect = (optionValue: T) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearch('');
    setFocusedIndex(0);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;

    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex((prev) => (prev + 1) % filteredOptions.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex((prev) => (prev - 1 + filteredOptions.length) % filteredOptions.length);
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredOptions[focusedIndex]) {
          handleSelect(filteredOptions[focusedIndex].value);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearch('');
        break;
    }
  };

  return (
    <div
      ref={selectRef}
      className={`relative ${className}`}
      onKeyDown={handleKeyDown}
    >
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          w-full px-4 py-2 min-h-[44px] md:min-h-0 text-left bg-dark-surface/80 backdrop-blur-sm border border-dark-border/30 rounded-xl
          flex items-center justify-between gap-2
          transition-all duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/50
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-dark-surface hover:border-dark-border/30 shadow-sm hover:shadow-md'}
          ${isOpen ? 'ring-2 ring-brand-400/50 border-brand-500/40 shadow-md' : ''}
        `}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        data-testid='unknown-action-btn'
        data-ux='action'
        data-interaction-id='unknown-action-btn'>
        <span className={selectedOption ? 'text-dark-text' : 'text-dark-muted'}>
          {selectedLabel}
        </span>
        <ChevronDown
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {/* Dropdown Menu - Portal-based with mobile-first responsive design */}
      {isOpen && (
        <Portal>
          <div
            ref={dropdownRef}
            className="fixed md:absolute"
            style={{
              top: window.innerWidth < 768 ? 'auto' : `${dropdownPosition.top + 8}px`,
              left: window.innerWidth < 768 ? 0 : `${dropdownPosition.left}px`,
              width: window.innerWidth < 768 ? '100%' : `${dropdownPosition.width}px`,
              bottom: window.innerWidth < 768 ? 0 : 'auto',
              zIndex: 99999, // Above modals (z-50) to prevent clipping
            }}
          >
            <div className="bg-dark-surface/95 backdrop-blur-lg border-t md:border border-dark-border/30 rounded-t-2xl md:rounded-xl shadow-2xl shadow-black/30 max-h-[70vh] md:max-h-[300px] overflow-auto">
              {/* Search Input */}
              {searchable && (
                <div className="sticky top-0 bg-dark-surface border-b border-dark-border/30 p-2 z-10">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-muted" />
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search..."
                      className="w-full pl-10 pr-4 py-2 bg-dark-bg border border-dark-border/30 rounded text-sm text-dark-text placeholder-dark-muted focus:outline-none focus:ring-2 focus:ring-brand-400"
                      onClick={(e) => e.stopPropagation()}
                      data-testid='unknown-search-input'
                      data-ux='search-edit'
                      data-interaction-id='unknown-search-input' />
                  </div>
                </div>
              )}

              {/* Options List */}
              <div role="listbox">
                {filteredOptions.length === 0 ? (
                  <div className="px-4 py-3 text-sm text-dark-muted text-center">
                    No options found
                  </div>
                ) : (
                  filteredOptions.map((option, index) => (
                    <button
                      key={String(option.value)}
                      type="button"
                      onClick={() => handleSelect(option.value)}
                      disabled={option.disabled}
                      className={`
                        w-full px-4 py-3 min-h-[44px] md:min-h-0 md:py-2 text-left text-sm transition-all duration-150
                        ${
                          option.disabled
                            ? 'text-dark-muted cursor-not-allowed'
                            : 'text-dark-text hover:bg-dark-surface/40 active:bg-dark-surface/60'
                        }
                        ${option.value === value ? 'bg-brand-500/10 font-semibold' : ''}
                        ${focusedIndex === index ? 'bg-dark-bg' : ''}
                      `}
                      role="option"
                      aria-selected={option.value === value}
                      data-testid='unknown-action-btn'
                      data-ux='action'
                      data-interaction-id='unknown-action-btn'>
                      {option.label}
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
}
